import { Badge, Button, Card } from "@design";
import { CheckCircle2, ExternalLink, FileEdit, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { DESKTOP_ONLY, useAvailableOn } from "@/responsive/surfaces";
import { AskUserCard } from "../AskUserCard";
import { useDraftContext } from "./DraftContext";
import type { DraftCardState } from "./draftModel";
import type { DraftStep } from "./useMultiStepCreate";

export interface DraftCardProps {
  title: string;
  entityLabel: string;
  state: DraftCardState;
  /** Changes an existing entity: Save instead of Create, Updated instead of Created. */
  isUpdate?: boolean;
  /** Set by the card once its Create/Save succeeded, before the chat's links catch up. */
  doneUuid?: string | null;
  /** The entity it created or saved. */
  entityUuid?: string;
  updatedAt?: string;
  summary: ReactNode;
  missingFields: string[];
  questions?: unknown[];
  answerable?: boolean;
  busy: boolean;
  steps?: DraftStep[];
  /** Why the card can't act (e.g. no team to create in). */
  blocked?: string | null;
  onCreate?: () => void;
  onRetry?: () => void;
  onOpenInForm?: () => void;
  detailPath?: (uuid: string) => string;
  createLabel?: string;
}

/** The frame every draft card shares: what the draft is, what's still needed, and Create / Save / Open. */
export function DraftCard(input: Readonly<DraftCardProps>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "draft" });
  const { streaming, readOnly, onAnswer } = useDraftContext();
  const done = Boolean(input.doneUuid) && input.state === "active";
  const props: DraftCardProps = done
    ? {
        ...input,
        state: input.isUpdate ? "updated" : "created",
        entityUuid: input.entityUuid ?? input.doneUuid ?? undefined,
        updatedAt: input.updatedAt ?? new Date().toISOString(),
      }
    : input;
  const { state, steps = [] } = props;
  const failed = steps.find((s) => s.status === "failed");
  const showSteps = steps.length > 1 && (props.busy || Boolean(failed));
  const questions = state === "active" && props.answerable && !readOnly ? (props.questions ?? []) : [];
  return (
    <div className="space-y-2">
      <Card>
        <section aria-label={t("label", { entity: props.entityLabel, name: props.title })} className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral" icon={<Sparkles size={12} />}>
              {props.entityLabel}
            </Badge>
            <Text variant="h3" className="min-w-0 break-words">
              {props.title}
            </Text>
          </div>
          {props.summary}
          {state === "active" && props.missingFields.length > 0 && (
            <Text variant="caption" tone="tertiary">
              {t("stillNeeded", { fields: props.missingFields.join(", ") })}
            </Text>
          )}
          {showSteps && (
            <ul className="space-y-1">
              {steps.map((s) => (
                <li key={s.key}>
                  <Text variant="body-sm" tone={s.status === "failed" ? "error" : "secondary"}>
                    {t(`step.${s.status}`, { step: s.label })}
                  </Text>
                </li>
              ))}
            </ul>
          )}
          {failed?.error && (
            <Text variant="body-sm" tone="error" role="alert">
              {failed.error}
            </Text>
          )}
          {state === "active" && props.blocked && !readOnly && (
            <Text variant="body-sm" tone="warning">
              {props.blocked}
            </Text>
          )}
          <DraftActions {...props} failed={Boolean(failed)} disabled={streaming || Boolean(props.blocked)} />
        </section>
      </Card>
      {questions.length > 0 && onAnswer && <AskUserCard prompt={{ question: "", questions }} onSubmit={onAnswer} />}
    </div>
  );
}

function DraftActions(props: Readonly<DraftCardProps & { failed: boolean; disabled: boolean }>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "draft" });
  const navigate = useNavigate();
  const { readOnly } = useDraftContext();
  // Build's pages are desktop-only; on a phone the card still creates, it just doesn't open them.
  const buildAvailable = useAvailableOn(DESKTOP_ONLY);
  const { state, entityUuid, detailPath } = props;
  if (state === "superseded") {
    return (
      <Text variant="caption" tone="tertiary">
        {t("superseded")}
      </Text>
    );
  }
  if (state === "created" || state === "updated") {
    // Open first: the "Updated …" time changes length as it ages, and must not move the button.
    return (
      <div className="flex flex-wrap items-center gap-2">
        {entityUuid && detailPath && buildAvailable && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<ExternalLink size={14} />}
            onClick={() => navigate(detailPath(entityUuid))}
          >
            {t("open")}
          </Button>
        )}
        <Badge variant="success" icon={<CheckCircle2 size={12} />}>
          {state === "created" ? t("created") : t("updated", { when: formatRelativeTime(props.updatedAt ?? "") })}
        </Badge>
      </div>
    );
  }
  if (readOnly) return null;
  const primary = props.failed ? props.onRetry : props.onCreate;
  const createLabel = props.createLabel ?? (props.isUpdate ? t("save") : t("create"));
  return (
    <div className="flex flex-wrap gap-2">
      {primary && (
        <Button variant="primary" size="sm" loading={props.busy} disabled={props.disabled} onClick={primary}>
          {props.failed ? t("retry") : createLabel}
        </Button>
      )}
      {props.onOpenInForm && buildAvailable && (
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<FileEdit size={14} />}
          disabled={props.disabled || props.busy}
          onClick={props.onOpenInForm}
        >
          {t("openInForm")}
        </Button>
      )}
    </div>
  );
}
